import { assert } from '@fe/core';
import { Button } from '@progress/kendo-react-buttons';
import { PageChangeEvent, Pager } from '@progress/kendo-react-data-tools';
import { DatePicker } from '@progress/kendo-react-dateinputs';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { NumericTextBox, TextBox } from '@progress/kendo-react-inputs';
import dayjs from 'dayjs';
import React, { useEffect } from 'react';
import { useBlocker } from 'react-router';
import { parseMyFleetFile } from '../../../../../../components/my-fleet/my-fleet-parser';
import { MyFleetAcidListItem } from '../../../../../../components/my-fleet/types';
import { DateTimeFormatter } from '../../../../../../formatters';
import { AddOutline, DownloadOutline, SaveOutline, TrashOutline } from '../../../../../../icons/icons';
import { MyFleetAcidListSource } from '../../../../../../udView/udview';
import { useAppDispatch, useAppSelector } from '../../../../../hooks/store-hooks';
import { setSetting } from '../../../../../state/slices/settings-slice';
import { SettingsNavTitle, useSettingsPageWidth } from '../../../context/SettingsNavContext';

const TAKE = 20;
export default function SettingsMyFleetAcidList() {
    useSettingsPageWidth('700px');
    const source = useAppSelector((state) => state.settings.myFleetAcidListSource);
    const isManualMode = source === MyFleetAcidListSource.manual;
    const [edited, setEdited] = React.useState(false);
    const [skip, setSkip] = React.useState(0);
    const manualAcidList = useAppSelector((x) => x.settings.myFleetAcidList);
    const fileAcidList = useAppSelector((x) => x.global.myFleetFileAcidList);
    const [items, setItems] = React.useState<MyFleetAcidListItem[]>([]);
    const [deleteAllVisible, setDeleteAllVisible] = React.useState(false);
    const [savePopupVisible, setSavePopupVisible] = React.useState(false);
    const dispatch = useAppDispatch();
    const blocker = useBlocker(edited);

    useEffect(() => {
        if (source === MyFleetAcidListSource.file) {
            setItems(fileAcidList);
        } else if (source === MyFleetAcidListSource.manual) {
            setItems(manualAcidList);
        } else if (source === MyFleetAcidListSource.desktopInterface) {
            // TODO: Desktop interface
        }
    }, [manualAcidList, fileAcidList, source]);
    const saveChanges = React.useCallback(() => {
        dispatch(setSetting('myFleetAcidList', items));
        setEdited(false);
    }, [items]);

    React.useEffect(() => {
        if (blocker.state === 'blocked') {
            setSavePopupVisible(true);
        }
    }, [blocker]);

    const handleAdd = (item: MyFleetAcidListItem) => {
        setEdited(true);
        const newItems = [item, ...items];
        setItems(newItems);
    };

    const handleDelete = (item: MyFleetAcidListItem) => {
        const idx = items.indexOf(item);
        if (idx == undefined) {
            return;
        }
        setEdited(true);
        const newItems = [...items.slice(0, idx), ...items.slice(idx + 1)];
        setItems(newItems);
        if (newItems.length <= skip) {
            setSkip(Math.max(skip - TAKE, 0));
        }
    };

    const handleDeleteAll = () => {
        setEdited(true);
        setItems([]);
        setSkip(0);
        setDeleteAllVisible(false);
    };

    const handleSaveChanges = () => {
        saveChanges();
        if (blocker.state === 'blocked') {
            setSavePopupVisible(false);
            blocker.proceed();
        }
    };

    const handleNoSaveChanges = () => {
        assert(blocker.state === 'blocked');
        blocker.proceed();
        setSavePopupVisible(false);
    };

    const handleCloseSaveChanges = () => {
        assert(blocker.state === 'blocked');
        blocker.reset();
        setSavePopupVisible(false);
    };

    const handleImport = async () => {
        if (!window.showOpenFilePicker) {
            return;
        }

        const result = await window.showOpenFilePicker({
            multiple: false,
            types: [
                {
                    accept: {
                        'text/plain': '.csv'
                    }
                }
            ]
        });

        if (!result || result.length < 1) {
            return;
        }

        const handle = result[0];
        const file = await handle.getFile();
        const text = await file.text();
        const items = parseMyFleetFile(text);
        setEdited(true);
        setItems(items);
    };

    return (
        <div className="h-100 min-h-0 overflow-auto">
            <div className="p-2 settings-group-content">
                <SettingsNavTitle>
                    <div className="d-flex justify-content-between gap-2 align-items-center h-100">
                        <div>ACID list</div>
                        {isManualMode && (
                            <Button svgIcon={SaveOutline} fillMode={'outline'} onClick={handleSaveChanges} disabled={!edited}>
                                Save
                            </Button>
                        )}
                    </div>
                </SettingsNavTitle>

                {savePopupVisible && (
                    <Dialog title="Acid list" modal={true} onClose={handleCloseSaveChanges}>
                        <div>Do you want to save pending changes?</div>
                        <DialogActionsBar layout="stretched">
                            <Button themeColor={'primary'} onClick={handleSaveChanges}>
                                Yes
                            </Button>
                            <Button fillMode={'outline'} onClick={handleNoSaveChanges}>
                                No
                            </Button>
                        </DialogActionsBar>
                    </Dialog>
                )}

                {deleteAllVisible && (
                    <Dialog height={190} title="Clear all" modal={true} onClose={() => setDeleteAllVisible(false)}>
                        <div className="h-100">Do you want to delete all entries?</div>
                        <DialogActionsBar layout="stretched">
                            <Button themeColor={'primary'} onClick={handleDeleteAll}>
                                Yes
                            </Button>
                            <Button fillMode={'outline'} onClick={() => setDeleteAllVisible(false)}>
                                No
                            </Button>
                        </DialogActionsBar>
                    </Dialog>
                )}
                {isManualMode && (
                    <div>
                        <div className="d-flex gap-2 pb-2 justify-content-between">
                            <Button fillMode="solid" themeColor="primary" svgIcon={DownloadOutline} onClick={handleImport}>
                                Import file
                            </Button>
                            <Button fillMode="outline" svgIcon={TrashOutline} onClick={() => setDeleteAllVisible(true)}>
                                Clear List
                            </Button>
                        </div>
                        <AddAcidItem itemAdded={handleAdd} />
                        <div className="border-bottom mt-2"></div>
                    </div>
                )}

                {!isManualMode && <div className="secondary-text">List is only editable in manual mode.</div>}

                <table className="cobalt-table cobalt-alternate overflow-auto w-100 mt-2">
                    <thead>
                        <tr>
                            <th>Aircraft ID</th>
                            <th>Scratchpad</th>
                            <th>Origin</th>
                            <th>Departure day</th>
                            <th>Departure hour</th>
                            <th className="text-end"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.slice(skip, skip + TAKE).map((listItem, i) => (
                            <tr key={i}>
                                <td className="ps-2">{listItem.acid}</td>
                                <td>{listItem.scratchpad || '-'}</td>
                                <td>{listItem.origin || '-'}</td>
                                <td>{DateTimeFormatter.formatDate(listItem.depDate) || '-'}</td>
                                <td>{listItem.depHour || '-'}</td>
                                <td className="text-end">
                                    <Button
                                        fillMode="outline"
                                        size="small"
                                        svgIcon={TrashOutline}
                                        disabled={!isManualMode}
                                        onClick={() => handleDelete(listItem)}
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                <Pager total={items.length} skip={skip} take={TAKE} onPageChange={(event: PageChangeEvent) => setSkip(event.skip)} />
            </div>
        </div>
    );
}

function AddAcidItem(props: { itemAdded: (item: MyFleetAcidListItem) => void }) {
    const [acid, setAcid] = React.useState('');
    const [origin, setOrigin] = React.useState('');
    const [scratchpad, setScratchpad] = React.useState('');
    const [departureDate, setDepartureDate] = React.useState<Date | null>(null);
    const [departureHour, setDepartureHour] = React.useState<number | null>(null);

    const handleAdd = () => {
        props.itemAdded({
            acid: acid.toUpperCase(),
            origin: origin.toUpperCase(),
            scratchpad,
            depDate: departureDate ? dayjs(departureDate).utcOffset(0, true).unix() : null,
            depHour: departureHour
        });
        setAcid('');
        setOrigin('');
        setScratchpad('');
        setDepartureDate(null);
        setDepartureHour(null);
    };

    return (
        <div>
            <div className="d-flex gap-2">
                <Button disabled={!acid} fillMode="solid" themeColor="primary" svgIcon={AddOutline} onClick={handleAdd} />
                <TextBox value={acid} placeholder="Aircraft ID*" className="input-uppercase" onChange={(e) => setAcid(e.value as string)} />
                <TextBox value={origin} placeholder="Origin" className="input-uppercase" onChange={(e) => setOrigin(e.value as string)} />
                <DatePicker value={departureDate} placeholder="Dep. day" onChange={(e) => setDepartureDate(e.value)} />
                <NumericTextBox
                    min={0}
                    max={23}
                    value={departureHour}
                    placeholder="Dep. hour"
                    onChange={(e) => setDepartureHour(e.value)}
                />
            </div>
            <div className="d-flex gap-2 pt-2">
                <TextBox value={scratchpad} placeholder="Scratchpad" onChange={(e) => setScratchpad(e.value as string)} maxLength={15} />
            </div>
        </div>
    );
}
